import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const AgentTranslationModal = ({
    currentLanguageId,
    onClose,
    onTranslate,
    heightClass = "vh-25",
    singleClickTranslate = false,
}) => {
    const API_BASE_URL = '/pceapi/genbi';
    const headers = {
        'Content-Type': 'application/json',
        Authorization: GetCookie('access_token')
    };
    const navigate = useNavigate();

    const [languages, setLanguages] = useState({});
    const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [filteredLanguages, setFilteredLanguages] = useState([]);
    const [selectedLanguageId, setSelectedLanguageId] = useState(null);
    const [error, setError] = useState('');
    const popupRef = useRef(null);

    // Fetch languages on mount
    useEffect(() => {
        fetchLanguages();
    }, []);

    // Filter languages when search or languages change
    useEffect(() => {
        if (!languages || Object.keys(languages).length === 0) {
            setFilteredLanguages([]);
            return;
        }

        const languageEntries = Object.entries(languages);

        if (searchInput === '') {
            setFilteredLanguages(languageEntries);
        } else {
            const lowercasedInput = searchInput.toLowerCase();
            const filtered = languageEntries.filter(([id, name]) =>
                name.toLowerCase().includes(lowercasedInput)
            );
            setFilteredLanguages(filtered);
        }
    }, [searchInput, languages]);

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                onClose();
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);


    const fetchLanguages = async () => {
        setIsLoadingLanguages(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/langs`,
                { headers }
            );

            const data = response.data;

            if (data.status === 'NotAuthorized') {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (data.status === 'success') {
                setLanguages(data.langs || {});
            } else {
                setError(data.message || 'Failed to fetch languages');
            }
        } catch (error) {
            console.error('Error fetching languages:', error);
            setError(error.message || 'Network error occurred while fetching languages');
        } finally {
            setIsLoadingLanguages(false);
        }
    };


    const handleSelectLanguage = (langId) => {
        if (parseInt(langId) === currentLanguageId) {
            return;
        }
        if (singleClickTranslate) {
            onTranslate(parseInt(langId));
            return;
        }
        setSelectedLanguageId(parseInt(langId));
        setError('');
    };

    const handleTranslateClick = () => {
        if (!selectedLanguageId) {
            setError('Please select a target language');
            return;
        }
        onTranslate(selectedLanguageId);
    };

    return (
        <div ref={popupRef} className="ev-message-card2 ev-card-3 translate-dropdown-popup min-w-200 p-0 end-0">

            <div className="p-2 pb-0 pt-1">
                <input
                    type="text"
                    className="form-control search-input ms-0 ps-3 ev-xsmall-p-blue"
                    placeholder="Search languages..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    autoFocus
                />
            </div>

            <div className={`scrollable-element p-2 ${heightClass}`}>
                {isLoadingLanguages && (
                    <div className="text-center p-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}

                {!isLoadingLanguages && filteredLanguages.length === 0 && (
                    <p className="ev-small-p-blue text-center mt-3">No languages found</p>
                )}

                {!isLoadingLanguages && filteredLanguages.length > 0 && (
                    <div>
                        {filteredLanguages.map(([id, name]) => {
                            const isCurrentLanguage = parseInt(id) === currentLanguageId;
                            const isSelected = parseInt(id) === selectedLanguageId;

                            return (
                                <div
                                    key={id}
                                    className={`mb-1 ev-btn-small ev-radius-xsmall ${isCurrentLanguage ? 'opacity-50' : 'cursor-pointer'} ${isSelected ? 'list-element-selected' : ''}`}
                                    onClick={() => !isCurrentLanguage && handleSelectLanguage(id)}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <span className={`${isSelected ? 'ev-small-p-light' : 'ev-xsmall-p-blue'} mb-0 ev-bold-reg`}>
                                                <i className=""></i>&nbsp; {name}
                                            </span>
                                        </div>
                                        <div>
                                            {isCurrentLanguage && (
                                                <span className="ev-message-card3 ev-btn-xsmall">Current</span>
                                            )}
                                            {isSelected && !isCurrentLanguage && (
                                                <i className=" text-success"></i>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {error && (
                <div className="px-2">
                    <div className="ev-alert-card-warning ev-btn-xsmall">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        {error}
                    </div>
                </div>
            )}

            {!singleClickTranslate && (
                <div className="d-flex justify-content-end p-2 pb-1 pe-1">
                    <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-bold-reg ev-btn-small ms-1"
                        onClick={handleTranslateClick}
                        disabled={!selectedLanguageId}
                    >
                        Translate
                    </button>
                </div>
            )}
        </div>
    );
};

export default AgentTranslationModal;